import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { AppButton, Checkbox, CountBadge } from '@clickguard/design-system'
import FilterLines from '@untitled-ui/icons-react/build/esm/FilterLines'
import ChevronDown from '@untitled-ui/icons-react/build/esm/ChevronDown'
import ChevronRight from '@untitled-ui/icons-react/build/esm/ChevronRight'

export type FilterOption = {
	value: string
	label: string
	/**
	 * How many rows this option WOULD match (DEV-2422 §2.1). Optional, because the
	 * Credentials tab's groups do not carry one. Counted against the other groups'
	 * current picks, not against this group's own — otherwise ticking one platform
	 * would drop every other platform's count to zero, and the menu would stop being
	 * able to tell you what else is there.
	 */
	count?: number
}

export type FilterGroup = {
	/** Stable key — also the key under which selections are stored in state. */
	key: string
	title: string
	options: FilterOption[]
}

/** Map of group key → selected option values. */
export type ConnectionsFilterState = Record<string, string[]>

type Props = {
	groups: FilterGroup[]
	value: ConnectionsFilterState
	onChange: (next: ConnectionsFilterState) => void
}

const toggle = (list: string[] | undefined, value: string): string[] => {
	const arr = list ?? []
	return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

/**
 * Connections Filters dropdown (DEV-2352 §8.2) — master-detail multi-select,
 * shared by the Ad Accounts and Credentials tabs. Left pane lists the filter
 * groups (each with a DS CountBadge); hovering a group flies its checkbox
 * options out to the right. All groups are multi-select and compose AND with
 * the active list. "Clear filters" resets every group.
 *
 * Built on @radix-ui/react-dropdown-menu (the option rows are plain buttons,
 * not DropdownMenu.Item, so the submenu stays open across multi-select clicks).
 */
export const ConnectionsFilters = ({ groups, value, onChange }: Props) => {
	const total = groups.reduce(
		(sum, g) => sum + (value[g.key]?.length ?? 0),
		0
	)

	return (
		<DropdownMenu.Root modal={false}>
			<DropdownMenu.Trigger asChild>
				<AppButton
					variant="secondary"
					roundness="default"
					size="sm"
					leftIcon={<FilterLines className="h-4 w-4" />}
					rightIcon={
						<span className="flex items-center gap-1.5">
							{total > 0 && <CountBadge count={total} />}
							<ChevronDown className="h-4 w-4" />
						</span>
					}
					label="Filters"
				/>
			</DropdownMenu.Trigger>

			<DropdownMenu.Portal>
				<DropdownMenu.Content
					sideOffset={5}
					align="start"
					className="flex flex-col bg-popover rounded-2xl shadow-dropShadow p-1.5 w-[220px] focus:outline-none z-50"
				>
					{groups.map((group) => {
						const selected = value[group.key] ?? []
						return (
							<DropdownMenu.Sub key={group.key}>
								<DropdownMenu.SubTrigger className="flex items-center justify-between px-3 py-2 rounded-md cursor-pointer hover:bg-popover-hover data-[state=open]:bg-popover-hover focus:outline-none text-[14px] text-[color:var(--text-primary)]">
									{group.title}
									<div className="flex items-center gap-2 pl-3">
										<CountBadge count={selected.length} />
										<ChevronRight className="h-4 w-4 text-[color:var(--text-tertiary,#80808a)]" />
									</div>
								</DropdownMenu.SubTrigger>
								<DropdownMenu.Portal>
									<DropdownMenu.SubContent
										sideOffset={4}
										className="flex flex-col bg-popover rounded-2xl shadow-dropShadow p-1.5 w-[220px] max-h-[450px] overflow-y-auto focus:outline-none z-50"
									>
										{group.options.map((opt) => (
											<button
												key={opt.value}
												type="button"
												onClick={() =>
													onChange({
														...value,
														[group.key]: toggle(selected, opt.value)
													})
												}
												className="flex w-full items-center gap-2 px-3 py-2 rounded-md text-left hover:bg-popover-hover focus:outline-none"
											>
												<Checkbox
													size="sm"
													checked={selected.includes(opt.value)}
													readOnly
													tabIndex={-1}
													className="pointer-events-none"
													label={opt.label}
												/>
												{opt.count !== undefined && (
													<span className="ml-auto shrink-0 text-[12px] text-[rgb(var(--cg-text-secondary))]">
														{opt.count}
													</span>
												)}
											</button>
										))}
									</DropdownMenu.SubContent>
								</DropdownMenu.Portal>
							</DropdownMenu.Sub>
						)
					})}

					<div className="my-1.5 h-px bg-t-traffic" />

					<button
						type="button"
						className="px-3 py-2 text-left text-[14px] text-[color:var(--text-primary)] hover:bg-popover-hover rounded-md disabled:opacity-40"
						disabled={total === 0}
						onClick={() =>
							onChange(
								groups.reduce(
									(acc, g) => ({ ...acc, [g.key]: [] }),
									{} as ConnectionsFilterState
								)
							)
						}
					>
						Clear filters
					</button>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	)
}

/** Group definitions for the Ad Accounts tab. */
export const AD_ACCOUNT_FILTER_GROUPS: FilterGroup[] = [
	{
		key: 'platforms',
		title: 'Ad platforms',
		options: [
			{ value: 'GADS', label: 'Google Ads' },
			{ value: 'FADS', label: 'Meta Ads' },
			{ value: 'BADS', label: 'Microsoft Ads' }
		]
	},
	{
		// DEV-2422 §2.1. Acts on the CHILD rows: an account survives when at least one of
		// its campaigns matches. `REMOVED` is a bucket here but deliberately not on the
		// breadcrumb pills — see `campaign-status.ts`.
		key: 'campaignStatuses',
		title: 'Campaign status',
		options: [
			{ value: 'ACTIVE', label: 'Enabled' },
			{ value: 'PAUSED', label: 'Paused' },
			{ value: 'REMOVED', label: 'Removed' }
		]
	},
	{
		// Renamed from "Protection status" with the column it filters (§2.3). Same
		// underlying value — `adAccount.status`.
		key: 'protectionStatuses',
		title: 'Click detection',
		options: [
			{ value: 'ON', label: 'On' },
			{ value: 'OFF', label: 'Off' }
		]
	}
]

/** Group definitions for the Credentials tab (platforms only). */
export const CREDENTIAL_FILTER_GROUPS: FilterGroup[] = [
	{
		key: 'platforms',
		title: 'Ad platforms',
		options: [
			{ value: 'GADS', label: 'Google Ads' },
			{ value: 'FADS', label: 'Meta Ads' },
			{ value: 'BADS', label: 'Microsoft Ads' }
		]
	}
]
